import { LegalClientRepresentativeClientInfoEntity } from 'api/types/entity';
import { isFinanceProject, isGlobalProject } from 'config';
import { formatToDate, parseFullName } from 'helpers';
import { TFunc } from 'libs/i18n';
import {
  CountryRiskBadges,
  CountryRiskField,
  CountryRiskRole,
} from 'modules/client/common/feature';
import { isLegalRepresentativeCompany } from 'modules/client/common/helpers';

const getRepresentativeInfoContentForFinance = (
  t: TFunc,
  representativeInfo?: LegalClientRepresentativeClientInfoEntity
) => {
  const isCompany = isLegalRepresentativeCompany(representativeInfo);

  if (isCompany) {
    return {
      name: representativeInfo?.company?.fullName ?? '',
      fields: [
        {
          label: t('client.client.representative.nameLabel'),
          value: parseFullName({
            firstName: representativeInfo?.firstName ?? '',
            lastName: representativeInfo?.lastName ?? '',
            middleName: representativeInfo?.middleName,
          }),
        },
        {
          label: t('client.client.representative.representativeCountryLabel'),
          value: representativeInfo?.registrationAddress?.country?.name,
          valueAdornment: (
            <CountryRiskBadges
              country={representativeInfo?.registrationAddress?.country}
              field={CountryRiskField.RegistrationAddress}
              role={CountryRiskRole.LegalClientRepresentative}
            />
          ),
        },
        {
          label: t('client.client.representative.representativeEmailLabel'),
          value: representativeInfo?.email,
        },
        {
          label: t('client.client.representative.representativePhoneLabel'),
          value: representativeInfo?.phoneNumber,
        },
        {
          label: t('client.client.representative.documentExpiryLabel'),
          value: representativeInfo?.identityDocument?.expirationDate
            ? formatToDate(representativeInfo.identityDocument.expirationDate)
            : undefined,
        },
      ],
    };
  }

  return {
    name: parseFullName({
      firstName: representativeInfo?.firstName ?? '',
      lastName: representativeInfo?.lastName ?? '',
      middleName: representativeInfo?.middleName,
    }),
    fields: [
      {
        label: t('client.client.representative.emailLabel'),
        value: representativeInfo?.email,
      },
      {
        label: t('client.client.representative.phoneLabel'),
        value: representativeInfo?.phoneNumber,
      },
      {
        label: t('client.client.representative.countryLabel'),
        value: representativeInfo?.registrationAddress?.country?.name,
        valueAdornment: (
          <CountryRiskBadges
            country={representativeInfo?.registrationAddress?.country}
            field={CountryRiskField.RegistrationAddress}
            role={CountryRiskRole.LegalClientRepresentative}
          />
        ),
      },
      {
        label: t('client.client.representative.documentExpiryLabel'),
        value: representativeInfo?.identityDocument?.expirationDate
          ? formatToDate(representativeInfo.identityDocument.expirationDate)
          : undefined,
      },
    ],
  };
};

const getRepresentativeInfoContentForGlobal = (
  t: TFunc,
  representativeInfo?: LegalClientRepresentativeClientInfoEntity
) => ({
  name: parseFullName({
    firstName: representativeInfo?.firstName ?? '',
    lastName: representativeInfo?.lastName ?? '',
    middleName: representativeInfo?.middleName,
  }),
  fields: [
    {
      label: t('client.client.representative.emailLabel'),
      value: representativeInfo?.email,
    },
    {
      label: t('client.client.representative.phoneLabel'),
      value: representativeInfo?.phoneNumber,
    },
    {
      label: t('client.client.representative.countryLabel'),
      value: representativeInfo?.residentialAddress?.country?.name,
      valueAdornment: (
        <CountryRiskBadges
          country={representativeInfo?.residentialAddress?.country}
          field={CountryRiskField.ResidentialAddress}
          role={CountryRiskRole.LegalClientRepresentative}
        />
      ),
    },
    {
      label: t('client.client.representative.documentExpiryLabel'),
      value: representativeInfo?.identityDocument?.expirationDate
        ? formatToDate(representativeInfo.identityDocument.expirationDate)
        : undefined,
    },
  ],
});

export const getRepresentativeInfoContent = (
  t: TFunc,
  representativeInfo?: LegalClientRepresentativeClientInfoEntity
) => {
  if (isFinanceProject()) {
    return getRepresentativeInfoContentForFinance(t, representativeInfo);
  }

  if (isGlobalProject()) {
    return getRepresentativeInfoContentForGlobal(t, representativeInfo);
  }

  return undefined;
};
